import { devtools } from 'zustand/middleware';
import { createStore, type StoreApi, useStore } from 'zustand';
import { useCookie } from 'react-use';
import { createContext, type PropsWithChildren, useContext, useEffect, useRef, useState } from 'react';
import { isWebGLEnabled } from '@pkgs/components/utils/webGLUtils';
import { safeSessionStorage } from '@pkgs/components/utils/storageUtils';
import { computeInitialMapState } from '@pkgs/components/utils/mapVisibilityUtils';
import { useMapTracking } from '@pkgs/components/hooks/useMapTracking';
import { useMapCookiePersistence } from '@pkgs/components/hooks/useMapCookiePersistence';
import { useBreakpoint } from '@pkgs/components/hooks/useBreakpoint';
import { useAppContext } from '@pkgs/components/hooks/useAppContext';
import { MAP_VIEW_RETURN_SESSION_KEY } from '@pkgs/components/consts/consts';

import { isProductionClient } from '../consts/environment';

export type MapVisibilityState = {
  hasMounted: boolean;
  isMapActive: boolean;
  isWebGLEnabled: boolean;
  actions: {
    setHasMounted: (hasMounted: boolean) => void;
    setIsMapActive: (isMapActive: boolean) => void;
    setIsWebGLEnabled: (isWebGLEnabled: boolean) => void;
    updateIsMapView: (value: boolean, skipTracking?: boolean) => void;
  };
};

type MapVisibilityStoreContextType = StoreApi<MapVisibilityState> | null;

const MapVisibilityStoreContext = createContext<MapVisibilityStoreContextType>(null);

export type MapVisibilityStoreProviderProps = PropsWithChildren<{
  onIsMapViewChange?: (isMapView: boolean) => void;
  isMapToggledOnInitially?: boolean;
}>;

const shouldRefreshPreMountStore = ({
  currentStore,
  previousInitialMapToggle,
  nextInitialMapToggle,
}: {
  currentStore: StoreApi<MapVisibilityState> | undefined;
  previousInitialMapToggle: boolean | undefined;
  nextInitialMapToggle: boolean | undefined;
}) =>
  Boolean(
    currentStore && previousInitialMapToggle === undefined && nextInitialMapToggle !== undefined && !currentStore.getState().hasMounted,
  );

const shouldDelayFallbackInitialization = ({
  hasRetriedDeferredInitialization,
  isMapToggledOnInitially,
  isMapToggledOnCookie,
  shouldReturnToMapView,
}: {
  hasRetriedDeferredInitialization: boolean;
  isMapToggledOnInitially: boolean | undefined;
  isMapToggledOnCookie: string | null | undefined;
  shouldReturnToMapView: boolean;
}) =>
  !hasRetriedDeferredInitialization &&
  isMapToggledOnInitially === undefined &&
  isMapToggledOnCookie === undefined &&
  !shouldReturnToMapView;

const createMapVisibilityStore = ({
  featureFlags,
  isMapToggledOnInitially,
  onIsMapViewChange,
  trackMapToggleClick,
}: {
  featureFlags: { isMapEnabled?: boolean };
  isMapToggledOnInitially?: boolean;
  onIsMapViewChange?: (isMapView: boolean) => void;
  trackMapToggleClick: (variant: 'on' | 'off') => void;
}) =>
  createStore<MapVisibilityState>()(
    devtools(
      (set, get) => ({
        hasMounted: false,
        isMapActive: isMapToggledOnInitially ?? false,
        isWebGLEnabled: true,
        actions: {
          setHasMounted: (hasMounted) => set({ hasMounted }, undefined, 'setHasMounted'),
          setIsMapActive: (isMapActive) => {
            return set({ isMapActive }, undefined, 'setIsMapActive');
          },
          setIsWebGLEnabled: (isWebGLEnabled) => set({ isWebGLEnabled }, undefined, 'setIsWebGLEnabled'),
          updateIsMapView: (value: boolean, skipTracking?: boolean) => {
            if (!featureFlags.isMapEnabled || !get().isWebGLEnabled) {
              get().actions.setIsMapActive(false);
              return;
            }
            if (!skipTracking) trackMapToggleClick(value ? 'on' : 'off');

            get().actions.setIsMapActive(value);

            if (onIsMapViewChange) onIsMapViewChange(value);
          },
        },
      }),
      { name: 'search-modules', store: 'map-visibility-store', enabled: !isProductionClient },
    ),
  );

export const MapVisibilityStoreProvider = ({ children, onIsMapViewChange, isMapToggledOnInitially }: MapVisibilityStoreProviderProps) => {
  const { featureFlags } = useAppContext();
  const { breakpoint } = useBreakpoint();
  const { trackMapToggleClick } = useMapTracking();
  const [isMapToggledOnCookie, setIsMapToggledOnCookie] = useCookie('ns-map-default-toggled-on');
  const [hasRetriedDeferredInitialization, setHasRetriedDeferredInitialization] = useState(false);
  /**
   * We are adopting the approach of creating the state of our context with Zustand to benefit from its state management
   * while limiting the state to the provider's subtree using React.Context
   * Find more info here: https://tkdodo.eu/blog/zustand-and-react-context
   */
  const mapVisibilityStoreRef = useRef<StoreApi<MapVisibilityState> | undefined>(undefined);
  const previousInitialMapToggleRef = useRef(isMapToggledOnInitially);

  const shouldRecreateStore = shouldRefreshPreMountStore({
    currentStore: mapVisibilityStoreRef.current,
    previousInitialMapToggle: previousInitialMapToggleRef.current,
    nextInitialMapToggle: isMapToggledOnInitially,
  });

  if (!mapVisibilityStoreRef.current || shouldRecreateStore) {
    // Recreate the scoped store while we are still pre-mount so children can
    // consume a browser-resolved initial map state during the same rerender.
    mapVisibilityStoreRef.current = createMapVisibilityStore({
      featureFlags,
      isMapToggledOnInitially,
      onIsMapViewChange,
      trackMapToggleClick,
    });
    previousInitialMapToggleRef.current = isMapToggledOnInitially;
  }

  const mapVisibilityStore = mapVisibilityStoreRef.current;

  // Initialize the map visibility once the breakpoint is known. If a consumer
  // derives `isMapToggledOnInitially` from browser-only state, that value may
  // only become available right after hydration, so we give it one more turn
  // before committing the plain desktop fallback of "map off".
  useEffect(() => {
    if (mapVisibilityStore.getState().hasMounted || breakpoint === undefined) return;

    // Check if user was in map view before navigating to expose
    const shouldReturnToMapView = safeSessionStorage.getItem(MAP_VIEW_RETURN_SESSION_KEY) === 'true';

    if (
      shouldDelayFallbackInitialization({
        hasRetriedDeferredInitialization,
        isMapToggledOnInitially,
        isMapToggledOnCookie,
        shouldReturnToMapView,
      })
    ) {
      // Give hydration one more turn to supply a browser-derived initial value
      // before we commit the plain desktop fallback of "map off".
      const timeoutId = window.setTimeout(() => {
        setHasRetriedDeferredInitialization(true);
      }, 0);

      return () => {
        window.clearTimeout(timeoutId);
      };
    }

    const isWebGLAvailable = isWebGLEnabled();
    const canRenderMap = featureFlags.isMapEnabled && isWebGLAvailable;
    mapVisibilityStore.getState().actions.setIsWebGLEnabled(isWebGLAvailable);

    // Clean up session storage after reading
    if (shouldReturnToMapView) {
      safeSessionStorage.removeItem(MAP_VIEW_RETURN_SESSION_KEY);
    }

    const shouldMapBeActive =
      canRenderMap &&
      computeInitialMapState({
        breakpoint,
        isMapToggledOnCookie: isMapToggledOnCookie ?? undefined,
        isMapToggledOnInitially,
        shouldReturnToMapView,
      });

    if (shouldReturnToMapView) {
      mapVisibilityStore.getState().actions.updateIsMapView(shouldMapBeActive, true);
    } else {
      mapVisibilityStore.getState().actions.setIsMapActive(shouldMapBeActive);
    }

    mapVisibilityStore.getState().actions.setHasMounted(true);
  }, [
    breakpoint,
    featureFlags.isMapEnabled,
    hasRetriedDeferredInitialization,
    isMapToggledOnCookie,
    isMapToggledOnInitially,
    mapVisibilityStore,
  ]);

  // Persist map state to cookie on desktop only (extracted to custom hook)
  useMapCookiePersistence(mapVisibilityStore, setIsMapToggledOnCookie);

  return <MapVisibilityStoreContext.Provider value={mapVisibilityStore}>{children}</MapVisibilityStoreContext.Provider>;
};

export const useMapVisibilityStore = <T,>(selector: (state: MapVisibilityState) => T): T => {
  const store = useContext(MapVisibilityStoreContext);
  if (!store) {
    throw new Error('Missing MapVisibilityStoreProvider');
  }
  return useStore(store, selector);
};
